import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "raviger";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { FormSkeleton } from "@/components/Common/SkeletonLoading";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import specimenDefinitionApi from "@/types/emr/specimenDefinition/specimenDefinitionApi";

import { SpecimenDefinitionRead } from "@/types/emr/specimenDefinition/specimenDefinition";
import { SpecimenDefinitionForm } from "./SpecimenDefinitionForm";

interface UpdateSpecimenDefinitionProps {
  facilityId: string;
  specimenSlug: string;
}

export function UpdateSpecimenDefinition({
  facilityId,
  specimenSlug,
}: UpdateSpecimenDefinitionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: specimenDefinition, isFetching } = useQuery({
    queryKey: ["specimenDefinitions", facilityId, specimenSlug],
    queryFn: query(specimenDefinitionApi.retrieveSpecimenDefinition, {
      pathParams: { facilityId, specimenSlug },
    }),
  });

  const { mutate: updateSpecimenDefinition, isPending: isUpdating } =
    useMutation({
      mutationFn: mutate(specimenDefinitionApi.updateSpecimenDefinition, {
        pathParams: { facilityId, specimenSlug },
      }),
      onSuccess: (specimenDefinition: SpecimenDefinitionRead) => {
        toast.success(t("specimen_definition_updated"));
        queryClient.invalidateQueries({
          queryKey: ["specimenDefinitions", facilityId],
        });
        navigate(`/specimen_definitions/${specimenDefinition.slug}`);
      },
    });

  if (isFetching) {
    return (
      <div
        className="p-4"
        role="status"
        aria-live="polite"
        aria-label={t("loading")}
      >
        <FormSkeleton rows={4} />
      </div>
    );
  }

  if (!specimenDefinition) {
    return <div>{t("not_found")}</div>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">
        {t("update_specimen_definition")}
      </h1>
      <SpecimenDefinitionForm
        initialData={specimenDefinition}
        onSubmit={(data) =>
          updateSpecimenDefinition({
            ...data,
            patient_preparation:
              data.patient_preparation?.filter((item) => item && item.code) ||
              [],
          })
        }
        isLoading={isUpdating}
      />
    </div>
  );
}
